import { isAEMAuthorMode } from '../../utils/aemAuthorMode'
import { calculateImwidthForImage } from '../../utils/calculateImwidthForImage'

/**
 * Flipbook
 */
export class GMXFlipbook extends HTMLElement {
	canvasEl: HTMLCanvasElement
	loadIntersectionObserver: IntersectionObserver
	flipOnRevealIntersectionObserver: IntersectionObserver

	private currentFrame: number
	get currentFrameNumber(): number {
		return this.currentFrame
	}

	private imageBlobs: Blob[]
	private isGrabbed = false
	private grabbedX: number

	private boundHandleLoadObservationCallback = this.handleLoadObservation.bind(this)
	private boundHandleObservationCallback = this.handleObservation.bind(this)
	private boundHandleDragStart = this._handleDragStart.bind(this)
	private boundHandleDragMove = this._handleDragMove.bind(this)
	private boundHandleDragEnd = this._handleDragEnd.bind(this)

	static get observedAttributes(): string[] {
		return ['data-image-url-format-mobile', 'data-image-url-format-desktop']
	}

	async attributeChangedCallback(name: string, oldValue: string, newValue: string): Promise<void> {
		if (
			((this.isMobile() && name === 'data-image-url-format-mobile') ||
				(!this.isMobile() && name === 'data-image-url-format-desktop')) &&
			oldValue &&
			newValue
		) {
			await this._preloadImages()
			this._updateCanvas()
		}
	}

	/**
	 * This is the image url format for the mobile experience
	 */
	get mobileFrameImageUrlFormat(): string {
		return this.getAttribute('data-image-url-format-mobile')
	}

	/**
	 * This is the image url format for the desktop experience
	 */
	get desktopFrameImageUrlFormat(): string {
		return this.getAttribute('data-image-url-format-desktop')
	}

	/**
	 * This is the number of frames the component will cycle through to make one full rotation
	 */
	get numberOfFrames(): number {
		// In AEM Author, the component isn't interactive so no need to go out and fetch all the images.  Just need
		// the first as a placeholder
		if (isAEMAuthorMode()) {
			return 1
		} else {
			return this.hasAttribute('data-number-of-frames')
				? parseInt(this.getAttribute('data-number-of-frames'))
				: null
		}
	}

	/**
	 * Starting Frame number.  Default to 1 if not set
	 */
	get startingFrame(): number {
		const frameNum = this.hasAttribute('data-starting-frame')
			? parseInt(this.getAttribute('data-starting-frame'), 10)
			: 1

		if (!isNaN(frameNum) && frameNum >= 1 && frameNum <= this.numberOfFrames) {
			return frameNum
		} else {
			return 1
		}
	}

	/**
	 * Return true if this instance is setup to be an autorotate on reveal
	 */
	get isAutoFlipOnReveal(): boolean {
		return this.hasAttribute('data-auto-flip-on-reveal') && this.dataset.autoFlipOnReveal.toLowerCase() !== 'false'
	}

	get isLoadingLazy(): boolean {
		return this.hasAttribute('data-loading') && this.dataset.loading.toLowerCase() === 'lazy'
	}

	async connectedCallback(): Promise<void> {
		if (this.isLoadingLazy) {
			this.loadIntersectionObserver = new IntersectionObserver(this.boundHandleLoadObservationCallback, {
				root: null,
				rootMargin: '500px 0px',
				threshold: 0,
			})
			this.loadIntersectionObserver.observe(this)
		} else {
			await this.init()
		}
	}

	disconnectedCallback(): void {
		this.loadIntersectionObserver?.disconnect()
		this.flipOnRevealIntersectionObserver?.disconnect()

		if (this.canvasEl) {
			this.canvasEl.removeEventListener('mousedown', this.boundHandleDragStart)
			this.canvasEl.removeEventListener('touchstart', this.boundHandleDragStart)

			this.canvasEl.removeEventListener('mousemove', this.boundHandleDragMove)
			this.canvasEl.removeEventListener('touchmove', this.boundHandleDragMove)

			this.canvasEl.removeEventListener('mouseup', this.boundHandleDragEnd)
			this.canvasEl.removeEventListener('touchend', this.boundHandleDragEnd)
		}
	}

	async init(): Promise<void> {
		if (!this.numberOfFrames) {
			window.console.log('ERROR:  Missing required attribute data-number-of-frames')
		}

		if (!this.currentFrame) {
			this.currentFrame = this.startingFrame
		}

		if (!this.imageBlobs?.length) {
			await this._preloadImages()
		}

		if (!this.canvasEl) {
			this.canvasEl = document.createElement('canvas') as HTMLCanvasElement
			this.appendChild(this.canvasEl)

			// Finally, set attribute that it loaded
			this.setAttribute('data-loaded', '')
		}

		this._updateCanvas()
		this._setupDragging()

		if (this.isAutoFlipOnReveal) {
			this.setupAutoFlipOnReveal()
		}
	}

	/**
	 * Preload all the images/frames for the flipbook so user doesn't experience any jank while flipping through
	 * @private
	 */
	private async _preloadImages(): Promise<void> {
		this.imageBlobs = []

		const promises = []
		for (let x = 1; x <= this.numberOfFrames; x++) {
			const urlFormat = this.isMobile()
				? this.mobileFrameImageUrlFormat
				: this.desktopFrameImageUrlFormat

			let url = urlFormat.replace(/{framenumber}/, x.toString().padStart(3, '0'))

			if (!urlFormat.match(/[?&]imwidth=/)) {
				const imwidth = calculateImwidthForImage(this.offsetWidth)
				url = url.match(/\?/)
					? `${url}&imwidth=${imwidth}`
					: `${url}?imwidth=${imwidth}`
			}

			// TODO:  Make better ???
			promises[x] = fetch(url, {
				headers: {
					Accept: 'image/avif,image/webp,image/apng,*/*',
				},
			})
				.then(response => response.blob())
				.then(response => {
					this.imageBlobs[x] = response
				})
		}

		await Promise.all(promises)

		const newEvent = new CustomEvent('gmxFlipbookImagesLoaded')
		this.dispatchEvent(newEvent)
	}

	private _handleDragStart(e: MouseEvent | TouchEvent): void {
		e.preventDefault()
		e.stopPropagation()

		this.canvasEl.classList.add('grabbed')
		this.isGrabbed = true

		if ('touches' in e && e.type === 'touchstart') {
			this.grabbedX = e.touches[0].clientX
		} else {
			if ('clientX' in e) {
				this.grabbedX = e.clientX
			}
		}
	}

	private _handleDragMove(e: MouseEvent | TouchEvent): void {
		if (this.isGrabbed) {
			let currX

			e.preventDefault()
			e.stopPropagation()

			if ('touches' in e && e.type === 'touchmove') {
				currX = e.touches[0].clientX
			} else {
				if ('clientX' in e) {
					currX = e.clientX
				}
			}

			if (currX + 20 < this.grabbedX) {
				this.currentFrame = this.currentFrame === this.numberOfFrames ? 1 : this.currentFrame + 1
				this._updateCanvas()
				this.grabbedX = currX
			} else if (currX - 20 > this.grabbedX) {
				this.currentFrame = this.currentFrame > 1 ? this.currentFrame - 1 : this.numberOfFrames
				this._updateCanvas()
				this.grabbedX = currX
			}
		}
	}

	private _handleDragEnd(e: MouseEvent | TouchEvent): void {
		e.preventDefault()
		e.stopPropagation()

		this.grabbedX = null
		this.isGrabbed = false

		this.canvasEl.classList.remove('grabbed')
	}

	/**
	 * Method called during init to set up the grabbing and dragging of the slides for mobile users (and desktop ???)
	 */
	private _setupDragging(): void {
		this.canvasEl.addEventListener('mousedown', this.boundHandleDragStart)
		this.canvasEl.addEventListener('touchstart', this.boundHandleDragStart)

		this.canvasEl.addEventListener('mousemove', this.boundHandleDragMove)
		this.canvasEl.addEventListener('touchmove', this.boundHandleDragMove)

		this.canvasEl.addEventListener('mouseup', this.boundHandleDragEnd)
		this.canvasEl.addEventListener('touchend', this.boundHandleDragEnd)
	}

	/**
	 * Draw a given image in the canvas area
	 * @private
	 */
	private _updateCanvas(): void {
		const img = new Image()

		const imgURL = window.URL.createObjectURL(this.imageBlobs[this.currentFrame])

		img.onload = () => {
			window.URL.revokeObjectURL(imgURL)

			if (!this.canvasEl.getAttribute('height')) {
				this.canvasEl.height = img.height
				this.canvasEl.width = img.width
			}

			const ctx = this.canvasEl.getContext('2d')

			ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
			ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height)
		}

		img.src = imgURL
	}

	/**
	 * Check if the screen is mobile size
	 */
	isMobile(): boolean {
		return !window.matchMedia('screen and (min-width: 900px)').matches
	}

	setupAutoFlipOnReveal(): void {
		this.flipOnRevealIntersectionObserver = new IntersectionObserver(this.boundHandleObservationCallback, {
			root: null,
			rootMargin: '0px',
			threshold: 1,
		})
		this.flipOnRevealIntersectionObserver.observe(this)
	}

	handleLoadObservation(entries: IntersectionObserverEntry[]): void {
		for (const entry of entries) {
			if (!entry.isIntersecting) continue
			this.loadIntersectionObserver?.unobserve(this)
			this.loadIntersectionObserver?.disconnect()
			void this.init()
			break
		}
	}

	/**
	 * Callback for when the IntersectionObserver triggers an observation event
	 * @param entries
	 */
	handleObservation(entries: IntersectionObserverEntry[]): void {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				this.autoRotateOnView()
				this.flipOnRevealIntersectionObserver.unobserve(this)
			}
		})
	}

	/**
	 * When invoked, will cause the flipbook to autorotate one quarter turn to indicate to the user that
	 * the element can be rotated
	 */
	autoRotateOnView(): void {
		const quarterOfFrames = this.numberOfFrames / 5

		let frameCount = 0

		const rotateInterval = setInterval(() => {
			this.currentFrame = this.currentFrame - 1 || this.numberOfFrames
			this._updateCanvas()

			frameCount++
			if (frameCount >= quarterOfFrames) {
				clearInterval(rotateInterval)
			}
		}, 100)
	}
}
if (!customElements.get('gmx-flipbook')) {
	customElements.define('gmx-flipbook', GMXFlipbook)
}
